/**
 * Maps an email's domain to a known webmail provider's "open inbox" URL.
 *
 * Returns null for unknown / corporate / malformed addresses so the caller
 * can conditionally render the "Open Gmail" / "Open Outlook" button only
 * when it actually points somewhere useful.
 *
 * The map is intentionally hardcoded — covers ~95% of consumer email with
 * zero network calls. Custom domains (corporate Google Workspace, self-
 * hosted Postfix, etc.) get null; we don't want to guess.
 */
export interface MailProvider {
  label: string;
  url: string;
}

const PROVIDERS: Record<string, MailProvider> = {
  'gmail.com':       { label: 'Open Gmail',       url: 'https://mail.google.com' },
  'googlemail.com':  { label: 'Open Gmail',       url: 'https://mail.google.com' },
  'outlook.com':     { label: 'Open Outlook',     url: 'https://outlook.live.com' },
  'hotmail.com':     { label: 'Open Outlook',     url: 'https://outlook.live.com' },
  'live.com':        { label: 'Open Outlook',     url: 'https://outlook.live.com' },
  'msn.com':         { label: 'Open Outlook',     url: 'https://outlook.live.com' },
  'yahoo.com':       { label: 'Open Yahoo Mail',  url: 'https://mail.yahoo.com' },
  'icloud.com':      { label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
  'me.com':          { label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
  'mac.com':         { label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
  'proton.me':       { label: 'Open Proton Mail', url: 'https://mail.proton.me' },
  'protonmail.com':  { label: 'Open Proton Mail', url: 'https://mail.proton.me' },
};

export function mailProviderFor(email: string): MailProvider | null {
  if (!email || typeof email !== 'string') return null;
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return null;
  const domain = email.slice(at + 1).toLowerCase();
  return PROVIDERS[domain] ?? null;
}
